<template>
  <div class="mail-priorities-settings">
    <v-container fluid>
      <!-- Page Header -->
      <div class="d-flex justify-space-between align-center mb-6">
        <div>
          <h1 class="text-h4 font-weight-bold mb-2">أولويات المراسلات</h1>
          <p class="text-body-1 text-grey">إدارة أولويات المراسلات في النظام</p>
        </div>
      </div>

      <!-- Content Card -->
      <v-card>
        <v-card-text class="pa-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <h2 class="text-h5">قائمة الأولويات</h2>
            <v-btn
            v-if="hasPermission('admin.mail-priorities.store')"
              v-action="{ module: 'mail-priorities', action: 'store' }"
              color="primary"
              prepend-icon="mdi-plus"
              @click="openAddPriorityDialog"
            >
              إضافة أولوية جديدة
            </v-btn>
          </div>

          <v-data-table
            :headers="priorityHeaders"
            :items="priorities"
            :search="prioritySearch"
            :loading="loading"
            color="primary"
            first-icon="mdi-chevron-left"
            last-icon="mdi-chevron-right"
            next-icon="mdi-chevron-right"
            prev-icon="mdi-chevron-left"
            show-current-page
            show-first-last-page
            show-first-page
            show-last-page
            show-next-page
            show-previous-page
            class="elevation-1"
          >
            <template #top>
              <v-text-field
                v-model="prioritySearch"
                prepend-inner-icon="mdi-magnify"
                label="بحث في الأولويات"
                variant="outlined"
                density="compact"
                class="mx-4 mt-4"
              ></v-text-field>
            </template>

            <template v-slot:[`item.color`]="{ item }">
              <v-chip :color="item.color" size="small" variant="flat">
                {{ item.name }}
              </v-chip>
            </template>

            <template v-slot:[`item.active`]="{ item }">
              <v-chip :color="item.active ? 'success' : 'error'" size="small" variant="tonal">
                {{ item.active ? 'نشط' : 'غير نشط' }}
              </v-chip>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                v-if="hasPermission('admin.mail-priorities.update')"
                v-action="{ module: 'mail-priorities', action: 'update' }"
                icon
                size="small"
                color="primary"
                variant="text"
                @click="editPriority(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="hasPermission('admin.mail-priorities.destroy')"
                v-action="{ module: 'mail-priorities', action: 'destroy' }"
                icon
                size="small"
                color="error"
                variant="text"
                @click="confirmDeleteDialog(item, 'priority')"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Priority Dialog -->
    <v-dialog v-model="showPriorityDialog" max-width="500px">
      <v-card>
        <v-card-title>
          {{ editingPriority ? 'تعديل الأولوية' : 'إضافة أولوية جديدة' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="priorityForm" v-model="valid" @submit.prevent="savePriority">
            <v-text-field
              v-model="priorityForm.name"
              label="اسم الأولوية"
              variant="outlined"
              required
              :rules="[v => !!v || 'اسم الأولوية مطلوب']"
              class="mb-4"
            ></v-text-field>
            
            <v-textarea
              v-model="priorityForm.desc"
              label="الوصف"
              variant="outlined"
              rows="3"
              class="mb-4"
            ></v-textarea>
            
            <v-text-field
              v-model="priorityForm.level"
              label="المستوى"
              type="number"
              variant="outlined"
              required
              :rules="[v => !!v || 'المستوى مطلوب', v => v > 0 || 'المستوى يجب أن يكون أكبر من صفر']"
              class="mb-4"
            ></v-text-field>
            
            <v-text-field
              v-model="priorityForm.color"
              label="اللون"
              variant="outlined"
              type="color"
              class="mb-4"
            ></v-text-field>
            
            <v-switch
              v-model="priorityForm.active"
              label="نشط"
              color="success"
              class="mb-4"
            ></v-switch>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showPriorityDialog = false">إلغاء</v-btn>
          <v-btn color="primary" @click="savePriority">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف {{ getDeleteMessage() }}؟
          <br><strong>{{ itemToDelete?.name }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" @click="confirmDelete">حذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import mailPriorityService from '@/services/mailPriorityService'
import { hasPermission, hasModule } from '@/utils/permissions'

export default {
  name: 'MailPrioritiesView',
  data() {
    return {
      loading: false,
      valid: false,
      prioritySearch: '',
      showPriorityDialog: false,
      editingPriority: null,
      priorities: [],
      priorityForm: {
        name: '',
        desc: '',
        level: 1,
        color: '#2196f3',
        active: true
      },
      priorityHeaders: [
        { title: 'الاسم', key: 'name', align: 'start' },
        { title: 'الوصف', key: 'desc', align: 'start' },
        { title: 'المستوى', key: 'level', align: 'center' },
        { title: 'اللون', key: 'color', align: 'center' },
        { title: 'الحالة', key: 'active', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      deleteDialog: false,
      itemToDelete: null,
      deleteType: ''
    }
  },
  async mounted() {
    await this.loadPriorities()
  },
  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    async loadPriorities() {
      this.loading = true
      try {
        const response = await mailPriorityService.getMailPriorities()
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.priorities = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          this.priorities = response.data
        } else {
          this.priorities = []
        }
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في جلب الأولويات'
        })
      } finally {
        this.loading = false
      }
    },

    openAddPriorityDialog() {
      this.editingPriority = null
      this.priorityForm = {
        name: '',
        desc: '',
        level: 1,
        color: '#2196f3',
        active: true
      }
      this.showPriorityDialog = true
    },

    editPriority(priority) {
      this.editingPriority = priority
      this.priorityForm = {
        name: priority.name,
        desc: priority.desc,
        level: priority.level,
        color: priority.color,
        active: Boolean(priority.active)
      }
      this.showPriorityDialog = true
    },

    async savePriority() {
      if (!this.$refs.priorityForm.validate()) return

      this.loading = true
      try {
        if (this.editingPriority) {
          await mailPriorityService.updateMailPriority(this.editingPriority.id, this.priorityForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم تحديث الأولوية بنجاح'
          })
        } else {
          await mailPriorityService.createMailPriority(this.priorityForm)
          this.$store.dispatch('alerts/showAlert', {
            type: 'success',
            message: 'تم إضافة الأولوية بنجاح'
          })
        }
        
        this.showPriorityDialog = false
        await this.loadPriorities()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حفظ الأولوية'
        })
      } finally {
        this.loading = false
      }
    },

    confirmDeleteDialog(item, type) {
      this.itemToDelete = item
      this.deleteType = type
      this.deleteDialog = true
    },

    async confirmDelete() {
      if (!this.itemToDelete) return

      this.loading = true
      try {
        await mailPriorityService.deleteMailPriority(this.itemToDelete.id)
        this.$store.dispatch('alerts/showAlert', {
          type: 'success',
          message: 'تم حذف الأولوية بنجاح'
        })
        
        this.deleteDialog = false
        await this.loadPriorities()
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          type: 'error',
          message: error.message || 'فشل في حذف الأولوية'
        })
      } finally {
        this.loading = false
      }
    },

    getDeleteMessage() {
      return 'الأولوية'
    }
  }
}
</script>

<style scoped>
.mail-priorities-settings {
  min-height: 100vh;
}
</style> 